<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from "vue";
import { useRouter } from "vue-router";
import MenuFiltrosOfertas from "./components/MenuFiltrosOfertas.vue";
import ListadoDeOfertasHistoricas from "./components/ListadoDeOfertasHistoricas.vue";
import ListadoDeOfertasHistoricasCard from "./components/ListadoDeOfertasHistoricasCard.vue";
import NavegacionTabla from "./components/NavegacionTabla.vue";
import SpinnerLoader from "@/components/Spinner/SpinnerLoader";
import { usehistorialDeOfertasStore } from "@/store/historialDeOfertasStore";

const historialDeOfertasStore = usehistorialDeOfertasStore();
const router = useRouter();

const screenWidth = ref(window.innerWidth);
const handleResize = () => (screenWidth.value = window.innerWidth);

const irASubasta = (id) =>
  router.push({ path: "/lotes", query: { id }, replace: false });

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  handleResize();
  historialDeOfertasStore.obtenerPerfilesFacturacionUsuario();
  await historialDeOfertasStore.obtenerConjuntoOfertas();
  historialDeOfertasStore.setSelectedTab(
    historialDeOfertasStore.nombre_pestañas_menu[0]
  );
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const paginaActual = computed(
  () => historialDeOfertasStore.selected_tab?.page ?? 1
);
const ultimaPagina = computed(
  () => historialDeOfertasStore.selected_tab?.last_page ?? 1
);

const goToPage = (page) => {
  const last = ultimaPagina.value || 1;
  const target = Math.min(Math.max(1, page), last);
  const estado = historialDeOfertasStore.selected_tab?.estado;
  if (!estado) return;
  if (target !== paginaActual.value) {
    historialDeOfertasStore.obtenerOfertasUsuario(estado, target);
  }
};
</script>

<template>
  <div class="container mt-xl-5 mt-5 pt-5 p-xl-0">
    <div class="spinner-overlay" v-if="historialDeOfertasStore.cargandoHistorial">
      <SpinnerLoader
        size="64px"
      />
    </div>
    <div class="d-flex align-items-center justify-content-center gap-2 mb-4">
      <h6 class="font-roboto fs-2 fw-600 m-0">Historial de ofertas</h6>

      <div
        class="tooltip-container"
        tabindex="0"
        aria-describedby="historial-tooltip"
      >
        <i
          class="fa-solid fa-circle-info fs-4 text-black"
          aria-hidden="true"
        ></i>
        <div id="historial-tooltip" class="custom-tooltip p-4" role="tooltip">
          Acá vas a ver tus ofertas ganadoras recientes, y en qué estado de
          aprobación se encuentran.
        </div>
      </div>
    </div>

    <div>
      <MenuFiltrosOfertas />
      <NavegacionTabla />

      <div v-if="screenWidth > 833">
        <ListadoDeOfertasHistoricas
          @irASubasta="irASubasta"
          :historialDeOfertasStore="historialDeOfertasStore"
        />
      </div>
      <div v-else>
        <ListadoDeOfertasHistoricasCard
          @irASubasta="irASubasta"
          :historialDeOfertasStore="historialDeOfertasStore"
        />
      </div>

      <div class="d-flex justify-content-center">
        <div
          class="d-flex justify-content-between align-items-center mt-3 px-3 w-100"
        >
          <nav class="mt-3">
            <div class="custom-pagination w-100">
              <a
                href="javascript:;"
                @click.prevent="goToPage(paginaActual - 1)"
                class="page-nav"
                :class="{ disabled: paginaActual === 1 }"
              >
                <i class="fa fa-chevron-left"></i> Anterior
              </a>

              <a
                v-for="page in ultimaPagina"
                :key="page"
                href="javascript:;"
                class="page-number fw-bold text-white"
                :class="{ active: page === paginaActual }"
                @click.prevent="goToPage(page)"
              >
                {{ page }}
              </a>

              <a
                href="javascript:;"
                @click.prevent="goToPage(paginaActual + 1)"
                class="page-nav"
                :class="{ disabled: paginaActual === ultimaPagina }"
              >
                Siguiente <i class="fa fa-chevron-right"></i>
              </a>
            </div>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
  outline: none;
}

.custom-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: 10;
  width: 220px;
  background-color: #fff;
  color: #333;
  text-align: left;
  border-radius: 6px;
  padding: 8px 10px;
  line-height: 1.4;
  border: 1px solid #eee;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  top: 130%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.2s;
  pointer-events: none;
}

.custom-tooltip::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent transparent #fff transparent;
}

@media (hover: hover) and (pointer: fine) {
  .tooltip-container:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
  }
}

.tooltip-container:focus-within .custom-tooltip,
.tooltip-container:focus .custom-tooltip {
  visibility: visible;
  opacity: 1;
}

.custom-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.page-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #d3d3d3;
  text-align: center;
  line-height: 28px;
  font-weight: 600;
  text-decoration: none;
  font-size: 12px;
}

.page-number.active {
  background-color: #000;
}

.page-nav {
  margin: 0 12px;
  font-size: 13px;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.page-number:hover:not(.active) {
  color: #000;
  background-color: #bfbfbf;
}

.page-nav:hover:not(.disabled) {
  opacity: 0.6;
}

.page-nav.disabled,
.page-number.disabled {
  pointer-events: none;
  opacity: 0.45;
}

@media (max-width: 576px) {
  .page-number {
    width: 26px;
    height: 26px;
    line-height: 26px;
    font-size: 11px;
  }
}
</style>
